import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
    X, Shield, ShieldOff, MicOff, Mic, UserMinus, AlertTriangle,
    Loader2, Clock, LogOut, Crown, Flag, Ban, Megaphone, Bell, BellOff, Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ActivityStatus } from './ui/ActivityStatus';
import { Button } from './ui/button';
import { useUserData } from './UserDataContext';
import { RoomService, lifecycleStateOf, toMillis } from '../services/RoomService';
import { buildRoomInviteLink } from '../utils/inviteCode';
import { copyToClipboard } from './utils/clipboard';
import {
    ROOM_MAX_MODS,
    ROOM_MUTE_MAX_MINUTES,
    ROOM_MUTE_MIN_MINUTES,
    canExtend,
    isMuted,
} from '../services/roomLifecycle';
import type { Room, RoomMember } from '../types';

interface RoomInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    room: Room;
    myId: string;
    /** Leave the room: performs the leave and announces it. */
    onLeave: () => void;
    /**
     * Exit a room this modal has ALREADY ended. Separate from onLeave because
     * ending is a complete action on its own — routing it back through the leave
     * handler ran leaveRoom a second time against a now-closed room, which
     * reports itself as closed and announced "Room ended" all over again. Two
     * identical popups for one tap.
     */
    onEnded: () => void;
}

/** Every option is inside the 5–60 minute window the rules also enforce. */
const MUTE_PRESETS = [5, 10, 15, 30, 60];
const EXTEND_PRESETS = [30, 60, 120];

/**
 * The room info menu: rules, people, and the controls that go with your role.
 *
 * The rules text lives here and only here — it is never auto-opened on join and
 * never pinned into the chat, so reading it is a deliberate act.
 *
 * Warnings are shown to the host and mods, not just to the person warned, so the
 * team shares one history when deciding whether to escalate.
 */
export function RoomInfoModal({ isOpen, onClose, room, myId, onLeave, onEnded }: RoomInfoModalProps) {
    const [tab, setTab] = useState<'rules' | 'people'>('rules');
    const [members, setMembers] = useState<RoomMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<Record<string, boolean>>({});
    const [muteTarget, setMuteTarget] = useState<string | null>(null);
    const [now, setNow] = useState(() => Date.now());
    const { blockUser } = useUserData();

    const isOwner = String(room.ownerId) === myId;
    const isMod = Array.isArray(room.moderators) && room.moderators.includes(myId);
    const isStaff = isOwner || isMod;
    const state = useMemo(() => lifecycleStateOf(room), [room]);

    // Read off the roster the modal already loads, so muting costs no extra read.
    const myMembership = useMemo(
        () => members.find(m => String(m.userId) === String(myId)) || null,
        [members, myId],
    );
    const notificationsMuted = (myMembership as any)?.notificationsMuted === true;

    const handleToggleRoomNotifications = () => {
        const next = !notificationsMuted;
        void run('roomNotifs',
            async () => {
                await RoomService.setRoomNotificationsMuted(room.id, myId, next);
                // Reflect it locally rather than refetching the whole roster.
                setMembers(prev => prev.map(m =>
                    String(m.userId) === String(myId)
                        ? ({ ...m, notificationsMuted: next } as RoomMember)
                        : m,
                ));
            },
            next ? 'Notifications off for this room' : 'Notifications on for this room');
    };

    const refresh = async () => {
        setLoading(true);
        try {
            const all = await RoomService.getRoomMembers(room.id);
            setMembers(all.filter(m => m.status === 'active'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        void refresh();
        const t = setInterval(() => setNow(Date.now()), 15_000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, room.id, room.memberCount]);

    const run = async (key: string, fn: () => Promise<any>, success: string) => {
        setBusy(prev => ({ ...prev, [key]: true }));
        try {
            await fn();
            toast.success(success);
            await refresh();
        } catch (e: any) {
            toast.error(e?.message || 'That did not go through.');
        } finally {
            setBusy(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleWarn = (target: RoomMember) => {
        const reason = window.prompt(`Warn ${target.userName || 'this member'} — what for?`);
        if (!reason || reason.trim().length < 3) {
            if (reason !== null) toast.error('Give a short reason; mods will see it.');
            return;
        }
        void run(`warn:${target.userId}`,
            () => RoomService.warnRoomMember(room.id, target.userId, myId, reason.trim()),
            'Warning recorded');
    };

    // Available to EVERY member, not just staff: Apple's UGC rules ask for a way
    // to report and to block an abusive user, and a member with no moderator
    // powers is exactly who needs it.
    const handleReportMember = (target: RoomMember) => {
        const reason = window.prompt(`Report ${target.userName || 'this member'} — what is going on?`);
        if (!reason || reason.trim().length < 4) {
            if (reason !== null) toast.error('Give a short reason so a moderator can act.');
            return;
        }
        void run(`report:${target.userId}`,
            () => RoomService.reportRoomMember(room.id, target.userId, myId, reason.trim()),
            'Reported — a moderator will review it');
    };

    const handleBlockMember = (target: RoomMember) => {
        if (!window.confirm(`Block ${target.userName || 'this member'}? You will stop seeing them anywhere in the app.`)) return;
        void run(`block:${target.userId}`,
            async () => { await blockUser(String(target.userId)); },
            'Blocked');
    };

    const handleShareToFeed = () => {
        const note = window.prompt('Say something to go with it (optional):', '');
        if (note === null) return;   // cancelled
        void run('shareFeed',
            () => RoomService.shareRoomToFeed(room.id, myId, note),
            'Posted to the feed');
    };

    const handleCopyInviteLink = async () => {
        const ok = await copyToClipboard(buildRoomInviteLink(room.id));
        if (ok) toast.success('Invite link copied');
        else toast.error('Could not copy the link');
    };

    const handleReportRoom = () => {
        const reason = window.prompt('What is wrong with this room?');
        if (!reason || reason.trim().length < 4) {
            if (reason !== null) toast.error('Give a short reason so a moderator can act.');
            return;
        }
        void run('reportRoom',
            () => RoomService.reportRoom(room.id, myId, reason.trim()),
            'Reported: a moderator will review this room');
    };

    const handleKick = (target: RoomMember) => {
        const reason = window.prompt(
            `Remove ${target.userName || 'this member'}? They will not be able to rejoin this room.`,
        );
        if (reason === null) return;
        void run(`kick:${target.userId}`,
            () => RoomService.kickRoomMember(room.id, target.userId, myId, reason.trim()),
            'Removed from the room');
    };

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0"
                        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                    />
                    <motion.div
                        initial={{ scale: 0.94, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.94, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md rounded-3xl overflow-hidden flex flex-col bg-white dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 shadow-2xl"
                        style={{ maxHeight: '86vh' }}
                    >
                        <div className="p-4 border-b border-black/10 dark:border-white/10">
                            <div className="flex items-center justify-between gap-3">
                                <h2 className="text-lg text-black dark:text-white truncate" style={{ fontWeight: 700 }}>
                                    {room.title}
                                </h2>
                                <Button
                                    variant="ghost"
                                    onClick={onClose}
                                    className="w-9 h-9 p-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10 shrink-0"
                                >
                                    <X className="w-4 h-4 text-black dark:text-white" />
                                </Button>
                            </div>
                            <div className="flex gap-2 mt-3">
                                {(['rules', 'people'] as const).map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTab(t)}
                                        className={`flex-1 py-2 rounded-xl transition-all ${
                                            tab === t
                                                ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                                                : 'bg-black/5 dark:bg-white/5 text-black/60 dark:text-white/60'
                                        }`}
                                        style={{ fontSize: 13, fontWeight: 700 }}
                                    >
                                        {t === 'rules' ? 'Rules' : `People (${room.memberCount || 0})`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 space-y-3">
                            {tab === 'rules' ? (
                                <>
                                    {room.rules?.trim() ? (
                                        <p className="text-black dark:text-white whitespace-pre-wrap" style={{ fontSize: 14, lineHeight: 1.6 }}>
                                            {room.rules}
                                        </p>
                                    ) : (
                                        <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 14 }}>
                                            The host has not set any rules for this room.
                                        </p>
                                    )}

                                    {isOwner && (
                                        <div className="pt-2 space-y-2">
                                            <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 11, fontWeight: 700 }}>
                                                HOST CONTROLS
                                            </p>

                                            {canExtend(state) ? (
                                                <div className="flex flex-wrap gap-2">
                                                    {EXTEND_PRESETS.map(mins => (
                                                        <button
                                                            key={mins}
                                                            disabled={busy.extend}
                                                            onClick={() => void run('extend',
                                                                () => RoomService.extendRoom(room.id, myId, mins),
                                                                'Room extended')}
                                                            className="px-3 py-2 rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white disabled:opacity-40"
                                                            style={{ fontSize: 13, fontWeight: 700 }}
                                                        >
                                                            <span className="flex items-center gap-1.5">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                +{mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 12 }}>
                                                    {state.extended
                                                        ? 'This room has already used its one extension.'
                                                        : 'Extending becomes available once the room is live.'}
                                                </p>
                                            )}

                                            <button
                                                onClick={() => {
                                                    if (!window.confirm('End the room now? Everything in it is deleted.')) return;
                                                    void run('end', () => RoomService.endRoom(room.id, myId), 'Room ended')
                                                        .then(() => { onClose(); onEnded(); });
                                                }}
                                                className="w-full rounded-xl text-white"
                                                style={{ background: '#E74C3C', fontSize: 13, fontWeight: 700, paddingTop: 10, paddingBottom: 10 }}
                                            >
                                                End room now
                                            </button>
                                        </div>
                                    )}

                                    {!isOwner && (
                                        <button
                                            onClick={() => { onClose(); onLeave(); }}
                                            className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white flex items-center justify-center gap-2"
                                            style={{ fontSize: 13, fontWeight: 700, paddingTop: 10, paddingBottom: 10 }}
                                        >
                                            <LogOut className="w-4 h-4" /> Leave room
                                        </button>
                                    )}
                                    {isOwner && (
                                        <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 11 }}>
                                            Leaving as host closes the room. Pass it to a mod first if you
                                            want it to keep running.
                                        </p>
                                    )}

                                    {/* Recruitment. A room is otherwise only findable
                                        by opening the Rooms tab, which is a problem
                                        for one still short of the three it needs to
                                        start. Any member can post it, not just the
                                        host. */}
                                    <button
                                        onClick={handleShareToFeed}
                                        disabled={busy.shareFeed}
                                        className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white flex items-center justify-center gap-2"
                                        style={{ fontSize: 13, fontWeight: 700, paddingTop: 10, paddingBottom: 10 }}
                                    >
                                        <Megaphone className="w-4 h-4" /> Repost this room to the YapTab
                                    </button>

                                    {/* Opens the public /rooms/:id landing page for
                                        anyone not signed in, so an invite can be sent
                                        outside the app and still make sense. */}
                                    <button
                                        onClick={handleCopyInviteLink}
                                        className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white flex items-center justify-center gap-2"
                                        style={{ fontSize: 13, fontWeight: 700, paddingTop: 10, paddingBottom: 10 }}
                                    >
                                        <Link2 className="w-4 h-4" /> Copy invite link
                                    </button>

                                    {/* Per-room, not app-wide: silencing one room
                                        leaves every other room and the rest of the
                                        app untouched. Stored on your own member
                                        document, so it follows you back if you
                                        rejoin. */}
                                    <button
                                        onClick={handleToggleRoomNotifications}
                                        disabled={busy.roomNotifs}
                                        className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white flex items-center justify-center gap-2"
                                        style={{ fontSize: 13, fontWeight: 700, paddingTop: 10, paddingBottom: 10 }}
                                    >
                                        {busy.roomNotifs
                                            ? <Loader2 className="w-4 h-4 animate-spin" />
                                            : notificationsMuted
                                                ? <BellOff className="w-4 h-4" />
                                                : <Bell className="w-4 h-4" />}
                                        {notificationsMuted
                                            ? 'Notifications off for this room'
                                            : 'Turn off notifications for this room'}
                                    </button>

                                    {/* Reporting the ROOM, not a message in it: its
                                        premise, title and rules. Goes straight to the
                                        app-wide Watcher queue, since the people who
                                        would action it in-room are the ones being
                                        reported. */}
                                    {!isOwner && (
                                        <button
                                            onClick={handleReportRoom}
                                            disabled={busy.reportRoom}
                                            className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white flex items-center justify-center gap-2"
                                            style={{ fontSize: 13, fontWeight: 700, paddingTop: 10, paddingBottom: 10 }}
                                        >
                                            <Flag className="w-4 h-4" /> Report this room
                                        </button>
                                    )}
                                </>
                            ) : loading ? (
                                <div className="flex items-center justify-center" style={{ paddingTop: 32, paddingBottom: 32 }}>
                                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}><Loader2 className="w-6 h-6" style={{ color: '#4478ea' }} /></motion.div>
                                </div>
                            ) : (
                                members.map((m) => {
                                    const targetIsOwner = String(room.ownerId) === String(m.userId);
                                    const targetIsMod = (room.moderators || []).includes(String(m.userId));
                                    const targetMuted = isMuted({ mutedUntil: toMillis(m.mutedUntil) }, now);
                                    const warnings = Array.isArray(m.warnings) ? m.warnings : [];
                                    const canModerate = isStaff && !targetIsOwner && String(m.userId) !== myId;

                                    return (
                                        <div key={m.userId} className="p-3 rounded-2xl bg-black/5 dark:bg-white/5">
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-10 h-10 shrink-0">
                                                    <AvatarImage src={m.userAvatar || ''} />
                                                    <AvatarFallback>{(m.userName || '?')[0]}</AvatarFallback>
                                                </Avatar>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <p className="text-black dark:text-white truncate" style={{ fontWeight: 700, fontSize: 14 }}>
                                                            {m.userName || 'Someone'}
                                                        </p>
                                                        {targetIsOwner && <Crown className="w-3.5 h-3.5 shrink-0" style={{ color: '#F39C12' }} />}
                                                        {targetIsMod && <Shield className="w-3.5 h-3.5 shrink-0" style={{ color: '#4478ea' }} />}
                                                    </div>
                                                    <p className="text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: 12 }}>
                                                        {targetMuted ? 'Muted' : targetIsOwner ? 'Host' : targetIsMod ? 'Mod' : 'Member'}
                                                        {isStaff && warnings.length > 0 ? ` · ${warnings.length} warning${warnings.length === 1 ? '' : 's'}` : ''}
                                                    </p>
                                                    <ActivityStatus userId={m.userId} style={{ fontSize: 11, marginTop: 2 }} />
                                                </div>
                                            </div>

                                            {/* Warning history — staff only. */}
                                            {isStaff && warnings.length > 0 && (
                                                <div className="mt-2 space-y-1">
                                                    {warnings.slice(-3).map((w: any, i: number) => (
                                                        <p key={i} className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 11 }}>
                                                            <AlertTriangle className="w-3 h-3 mr-1" style={{ color: '#F39C12', display: 'inline' }} />
                                                            {w.reason} — {w.byName || 'a mod'}
                                                        </p>
                                                    ))}
                                                </div>
                                            )}

                                            {String(m.userId) !== myId && (
                                                <div className="flex flex-wrap gap-2 mt-3">
                                                    <button
                                                        onClick={() => handleReportMember(m)}
                                                        disabled={busy[`report:${m.userId}`]}
                                                        className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white flex items-center gap-1.5"
                                                        style={{ fontSize: 12, fontWeight: 700 }}
                                                    >
                                                        <Flag className="w-3.5 h-3.5" /> Report
                                                    </button>
                                                    <button
                                                        onClick={() => handleBlockMember(m)}
                                                        disabled={busy[`block:${m.userId}`]}
                                                        className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white flex items-center gap-1.5"
                                                        style={{ fontSize: 12, fontWeight: 700 }}
                                                    >
                                                        <Ban className="w-3.5 h-3.5" /> Block
                                                    </button>
                                                </div>
                                            )}

                                            {canModerate && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    <button
                                                        onClick={() => handleWarn(m)}
                                                        disabled={busy[`warn:${m.userId}`]}
                                                        className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white disabled:opacity-40"
                                                        style={{ fontSize: 12, fontWeight: 700 }}
                                                    >
                                                        Warn
                                                    </button>

                                                    {targetMuted ? (
                                                        <button
                                                            onClick={() => void run(`unmute:${m.userId}`,
                                                                () => RoomService.unmuteRoomMember(room.id, m.userId, myId),
                                                                'Unmuted')}
                                                            className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white flex items-center gap-1.5"
                                                            style={{ fontSize: 12, fontWeight: 700 }}
                                                        >
                                                            <Mic className="w-3.5 h-3.5" /> Unmute
                                                        </button>
                                                    ) : muteTarget === m.userId ? (
                                                        // Duration is the mod's choice, bounded to 5–60 minutes.
                                                        <div className="flex flex-wrap gap-1.5 w-full">
                                                            {MUTE_PRESETS.map(mins => (
                                                                <button
                                                                    key={mins}
                                                                    onClick={() => {
                                                                        setMuteTarget(null);
                                                                        void run(`mute:${m.userId}`,
                                                                            () => RoomService.muteRoomMember(room.id, m.userId, myId, mins),
                                                                            `Muted for ${mins} minutes`);
                                                                    }}
                                                                    className="py-1.5 rounded-lg text-white"
                                                                    style={{ background: '#4478ea', fontSize: 12, fontWeight: 700, paddingLeft: 10, paddingRight: 10 }}
                                                                >
                                                                    {mins}m
                                                                </button>
                                                            ))}
                                                            <button
                                                                onClick={() => setMuteTarget(null)}
                                                                className="py-1.5 rounded-lg bg-black/5 dark:bg-white/10 text-black dark:text-white"
                                                                style={{ fontSize: 12, fontWeight: 700, paddingLeft: 10, paddingRight: 10 }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setMuteTarget(m.userId)}
                                                            className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white flex items-center gap-1.5"
                                                            style={{ fontSize: 12, fontWeight: 700 }}
                                                            title={`${ROOM_MUTE_MIN_MINUTES}–${ROOM_MUTE_MAX_MINUTES} minutes`}
                                                        >
                                                            <MicOff className="w-3.5 h-3.5" /> Mute
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => handleKick(m)}
                                                        disabled={busy[`kick:${m.userId}`]}
                                                        className="px-3 py-1.5 rounded-xl text-white flex items-center gap-1.5 disabled:opacity-40"
                                                        style={{ background: '#E74C3C', fontSize: 12, fontWeight: 700 }}
                                                    >
                                                        <UserMinus className="w-3.5 h-3.5" /> Remove
                                                    </button>

                                                    {/* Mod appointment and ownership are the host's alone. */}
                                                    {isOwner && (
                                                        <>
                                                            <button
                                                                onClick={() => void run(`mod:${m.userId}`,
                                                                    () => RoomService.setRoomModerator(room.id, myId, m.userId, !targetIsMod),
                                                                    targetIsMod ? 'Mod removed' : 'Mod added')}
                                                                className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white flex items-center gap-1.5"
                                                                style={{ fontSize: 12, fontWeight: 700 }}
                                                                title={`Up to ${ROOM_MAX_MODS} mods`}
                                                            >
                                                                {targetIsMod
                                                                    ? <><ShieldOff className="w-3.5 h-3.5" /> Unmod</>
                                                                    : <><Shield className="w-3.5 h-3.5" /> Make mod</>}
                                                            </button>
                                                            {targetIsMod && (
                                                                <button
                                                                    onClick={() => {
                                                                        if (!window.confirm(`Hand the room to ${m.userName || 'this mod'}? You become a mod.`)) return;
                                                                        void run(`transfer:${m.userId}`,
                                                                            () => RoomService.transferRoomOwnership(room.id, myId, m.userId),
                                                                            'Ownership transferred');
                                                                    }}
                                                                    className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 text-black dark:text-white flex items-center gap-1.5"
                                                                    style={{ fontSize: 12, fontWeight: 700 }}
                                                                >
                                                                    <Crown className="w-3.5 h-3.5" /> Make host
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
}

export default RoomInfoModal;
